import { isNativeRoute } from '../config/nativeScreens';

// On the native floor screens, the keyboard's Enter key walks the form the way a
// native app's "Next" does: Enter in a field moves to the next field, so a
// weighing sheet is keyed without reaching up to tap each box. In the last field
// of a <form> Enter is left alone, so the form submits as it always did; outside
// a form the last field just closes the keyboard. The key label follows suit
// (`enterkeyhint`: Next / Go / Done).
//
// Scope: the open dialog / sheet / full-screen editor if there is one, else the
// page's <main>. Textareas keep Enter for new lines; selects, checkboxes and
// buttons are not stops — only fields that take typing.
const PHONE = 'screen and (max-width: 767.98px)';
const FIELD = 'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="file"]):not([type="button"]):not([type="submit"]):not([type="range"]):not([type="color"]):not([disabled]):not([readonly]), textarea:not([disabled]):not([readonly])';

const active = () => window.matchMedia?.(PHONE).matches && isNativeRoute(window.location.pathname);

function scopeOf(el) {
  return el.closest('form')
    || el.closest('[role="dialog"], [role="alertdialog"]')
    || el.closest('main')
    || document.body;
}

function fieldsIn(scope) {
  return Array.from(scope.querySelectorAll(FIELD)).filter((f) => f.getClientRects().length > 0);
}

function nextField(el) {
  const list = fieldsIn(scopeOf(el));
  const i = list.indexOf(el);
  return i >= 0 && i < list.length - 1 ? list[i + 1] : null;
}

export function installEnterNext() {
  if (typeof document === 'undefined' || !window.matchMedia) return () => {};

  const onFocus = (e) => {
    const el = e.target;
    if (!(el instanceof HTMLInputElement) || !el.matches(FIELD) || !active()) return;
    if (el.hasAttribute('enterkeyhint') && !el.hasAttribute('data-auto-enterkeyhint')) return;
    const hint = nextField(el) ? 'next' : el.closest('form') ? 'go' : 'done';
    el.setAttribute('enterkeyhint', hint);
    el.setAttribute('data-auto-enterkeyhint', '');
  };

  const onKey = (e) => {
    if (e.key !== 'Enter' || e.isComposing || e.defaultPrevented) return;
    const el = e.target;
    if (!(el instanceof HTMLInputElement) || !el.matches(FIELD) || !active()) return;
    // A combobox / search box that owns Enter (a pick list is open) keeps it.
    if (el.getAttribute('aria-expanded') === 'true') return;
    const next = nextField(el);
    if (next) {
      e.preventDefault();
      next.focus(); // a 0 in it is selected by utils/zeroSelect.js
      return;
    }
    if (!el.closest('form')) {
      e.preventDefault();
      el.blur();
    }
    // Last field of a form: let it submit.
  };

  document.addEventListener('focusin', onFocus);
  document.addEventListener('keydown', onKey);
  return () => {
    document.removeEventListener('focusin', onFocus);
    document.removeEventListener('keydown', onKey);
  };
}
